import { BaseMatchState, MATCH_STATE, GAME_TYPE, MatchSet, BOT_DIFFICULTY, MATCH_TYPE } from "../types/match";
import { cloneDeep } from "lodash";
import { playGameOverAudio } from "./sounds";
import { getBotDifficultyLocalStorage, getLegLocalStorage, getMatchTypeLocalStorage, getSetLocalStorage } from "./localStorage";
import { PlayerTeamIdx, TEAM } from "../types/teams";
import { getPlayersTeam } from "./teamLogic";
import { PLAYER } from "../types/players";
import { GameState, OnlineGameState } from "../types/game";

export const initMatchState: BaseMatchState = {
  matchState: MATCH_STATE.NOT_STARTED,
  matchType: getMatchTypeLocalStorage(),
  legs: getLegLocalStorage(),
  sets: getSetLocalStorage(),
  completed: {
    legs: [],
    sets: []
  },
  gameType: GAME_TYPE.NOT_SELECTED,
  botDifficulty: getBotDifficultyLocalStorage(),
};

function getNewMatch(): BaseMatchState {
  const match = cloneDeep(initMatchState);
  match.legs = getLegLocalStorage();
  match.sets = getSetLocalStorage();
  match.botDifficulty = getBotDifficultyLocalStorage();
  match.matchType = getMatchTypeLocalStorage();
  match.gameType = GAME_TYPE.NOT_SELECTED;
  return match;
}

function getPlayerLegWins(winner: TEAM | string, currentLegs: (GameState)[]): number {
  const wins = currentLegs.filter(l => l.winner === winner).length;
  return wins;
}

function getPlayerSetWins(winner: TEAM | string, currentSets: MatchSet[]): number {
  const wins = currentSets.filter(l => l.winner === winner).length;
  return wins;
}

export function handleEndLeg(matchState: BaseMatchState, finishedGameState: GameState): BaseMatchState {
  const newMatchState = cloneDeep(matchState);
  const winner = finishedGameState.winner;
  if (!winner) return newMatchState;
  playGameOverAudio();
  newMatchState.completed.legs.push(finishedGameState);
  const legWins = getPlayerLegWins(winner, newMatchState.completed.legs);
  const legWinsNeeded = Math.ceil(matchState.legs / 2);
  if (legWins < legWinsNeeded) return newMatchState;

  newMatchState.completed.sets.push({
    winner,
    legs: newMatchState.completed.legs
  });
  newMatchState.completed.legs = [];

  const setWins = getPlayerSetWins(winner, newMatchState.completed.sets);
  const setWinsNeeded = Math.ceil(matchState.sets / 2);
  if (setWins >= setWinsNeeded) {
    newMatchState.matchState = MATCH_STATE.FINISHED
  }

  return newMatchState;
}

export function newMatchHandler(): BaseMatchState {
  return getNewMatch();
}

/**
 * Returns true if the bot hits a shot according to its difficulty's odds
 * EASY: 25%, 
 * MEDIUM: 45%, 
 * HARD: 75%, 
 * PRO: 98%, 
 * @param botDifficulty
 * @returns 
 */
export function botHitChance(botDifficulty: BOT_DIFFICULTY): boolean {
  switch (botDifficulty) {
    case BOT_DIFFICULTY.EASY:
      return Math.random() < .25;
    case BOT_DIFFICULTY.MEDIUM:
      return Math.random() < .40;
    case BOT_DIFFICULTY.HARD:
      return Math.random() < .75;
    // case BOT_DIFFICULTY.PRO:
    //     return Math.random() < .98;
    default:
      return Math.random() < .50;
  }
}

export function continueUndoHandling(oldStates: (OnlineGameState)[], undoCanceled: boolean, online: boolean, onlinePlayer: PLAYER | null, gameState: (OnlineGameState), matchType: MATCH_TYPE): boolean {
  let continueHandling: boolean = true;
  if (oldStates.length === 0 && !online) {
    continueHandling = false;
  }
  if (matchType === MATCH_TYPE.BOT &&
    gameState[TEAM.ONE].players[0].totalThrows === 0) {
    continueHandling = false;
  }
  if (undoCanceled &&
    gameState.requests?.undo &&
    gameState.requests?.undo !== onlinePlayer) {
    continueHandling = false;
  }
  if (onlinePlayer &&
    !Boolean(gameState.requests?.undo) &&
    gameState[getPlayersTeam(onlinePlayer)].players[
      PlayerTeamIdx[onlinePlayer]
    ].totalThrows === 0) {
    continueHandling = false;
  }
  if (
    gameState?.requests?.undo === onlinePlayer) {
    continueHandling = false;
  }
  return continueHandling;
}

export function getNextThrower({ currentPlayer, team1 }: OnlineGameState): PLAYER {
  let nextPlayer: PLAYER = PLAYER.FIRST;
  const isDoubles = Boolean(team1.players.length > 1);
  switch (currentPlayer) {
    case PLAYER.FIRST:
      nextPlayer = PLAYER.SECOND;
      break;
    case PLAYER.SECOND:
      nextPlayer = isDoubles ? PLAYER.THIRD : PLAYER.FIRST;

      break;
    case PLAYER.THIRD:
      nextPlayer = PLAYER.FOURTH;

      break;
    case PLAYER.FOURTH:
      nextPlayer = PLAYER.FIRST;
      break;
  }

  return nextPlayer;
}